/**
 * useFabState — derives FAB visibility and creation constraints from app state.
 *
 * The FAB is shown ONLY when the ActionPanel is hidden on mobile:
 *   - Mobile + no-selection + not dragging + allowCreate
 *
 * Section creation is only allowed in container views (project/list)
 * where sections make positional sense.
 */

import { useViewContext } from "@/app/stores/viewContext"
import { useActionPanelState } from "@/features/action-panel/useActionPanelState"
import { useBreakpoint } from "@/shared/hooks/useBreakpoint"
import { useSetting } from "@/features/settings/hooks/useSetting"
import type { ViewId } from "@/features/views/viewRegistry"

// Views where sections can exist (container views with positional sorting)
const SECTION_VIEWS = new Set<ViewId>(["project", "list"])

export interface FabState {
  /** Whether the FAB should be visible */
  visible: boolean
  /** Whether section creation is allowed in the current view */
  allowSection: boolean
  /** Current view ID */
  viewId: ViewId
  /** Container ID (project/list ID) — needed for parentPath */
  containerId: string | null
}

export function useFabState(): FabState {
  const { isMobile } = useBreakpoint()
  const { context, visible: actionPanelVisible, allowCreate } = useActionPanelState()
  const viewId = useViewContext((s) => s.viewId)
  const containerId = useViewContext((s) => s.containerId)
  const { value: fabEnabled } = useSetting("#quick-add-fab", "true")

  // FAB visible when: mobile + ActionPanel is hidden (no-selection on mobile)
  // + user hasn't disabled it in settings
  // + not in views where task creation doesn't make sense
  const visible =
    isMobile &&
    context === "no-selection" &&
    actionPanelVisible &&
    allowCreate &&
    fabEnabled !== "false" &&
    viewId !== "recurring"

  const allowSection = SECTION_VIEWS.has(viewId)

  return { visible, allowSection, viewId, containerId }
}
